import {
  Button,
  Dialog,
  Field,
  Input,
  Portal,
  Stack,
  Text,
} from '@chakra-ui/react';
import { toaster } from '@mono/chakra';
import { isHttpError } from '@mono/http-hook';
import { FC, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

type FormValues = {
  name: string;
};

type ManageTeamModalProps = {
  isOpen: boolean;
  mode: 'create' | 'edit';
  defaultValues?: {
    id: string;
    name: string;
  };
  onClose: () => void;
  onSubmit: (values: FormValues) => Promise<void>;
};

const getErrorMessage = (error: unknown, fallback: string) => {
  if (isHttpError(error)) {
    const responseData = error.response?.data;
    if (responseData && typeof responseData === 'object') {
      const { error: errorMessage, message: responseMessage } =
        responseData as { error?: string; message?: string };
      return errorMessage ?? responseMessage ?? error.message ?? fallback;
    }
    return error.message ?? fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

const ManageTeamModal: FC<ManageTeamModalProps> = ({
  isOpen,
  mode,
  defaultValues,
  onClose,
  onSubmit,
}) => {
  const {
    handleSubmit,
    register,
    reset,
    formState,
  } = useForm<FormValues>({
    defaultValues: {
      name: defaultValues?.name ?? '',
    },
  });
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (defaultValues) {
      reset({ name: defaultValues.name }, { keepDirty: false });
    } else {
      reset({ name: '' }, { keepDirty: false });
    }
    setServerError(null);
  }, [defaultValues, reset, isOpen]);

  const closeModal = () => {
    onClose();
  };

  const handleFormSubmit: SubmitHandler<FormValues> = async (values) => {
    setServerError(null);

    try {
      await onSubmit(values);
      toaster.create({
        type: 'success',
        title:
          mode === 'create' ? 'Team created' : 'Team details updated',
        description:
          mode === 'create'
            ? 'Your new team is ready to go.'
            : 'Team information saved successfully.',
      });
      closeModal();
    } catch (error) {
      const message = getErrorMessage(
        error,
        mode === 'create'
          ? 'Unable to create team.'
          : 'Unable to update team.'
      );
      setServerError(message);
      toaster.create({
        type: 'error',
        title: mode === 'create' ? 'Failed to create team' : 'Failed to update team',
        description: message,
      });
    }
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={({ open }) => {
        if (!open) {
          closeModal();
        }
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.CloseTrigger />
            <Dialog.Header flexDirection={'column'}>
              <Dialog.Title>
                {mode === 'create' ? 'Create team' : 'Edit team'}
              </Dialog.Title>
              <Dialog.Description>
                {mode === 'create'
                  ? 'Add a new team to your organization.'
                  : 'Update the name for this team.'}
              </Dialog.Description>
            </Dialog.Header>
            <Dialog.Body>
              <form
                id="manage-team-form"
                onSubmit={handleSubmit(handleFormSubmit)}
              >
                <Stack gap="4">
                  <Field.Root invalid={Boolean(formState.errors.name)}>
                    <Field.Label>Team name</Field.Label>
                    <Input
                      autoFocus
                      placeholder="Growth Team"
                      {...register('name', {
                        required: 'Team name is required.',
                        minLength: {
                          value: 2,
                          message: 'Name must contain at least 2 characters.',
                        },
                      })}
                    />
                    <Field.HelperText>
                      Team names help members understand where to collaborate.
                    </Field.HelperText>
                    <Field.ErrorText>
                      {formState.errors.name?.message}
                    </Field.ErrorText>
                  </Field.Root>
                  {serverError ? (
                    <Text color="fg.error" fontSize="sm">
                      {serverError}
                    </Text>
                  ) : null}
                </Stack>
              </form>
            </Dialog.Body>
            <Dialog.Footer gap="3" justifyContent="flex-end">
              <Button
                variant="ghost"
                onClick={closeModal}
                disabled={formState.isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="manage-team-form"
                loading={formState.isSubmitting}
                colorPalette="primary"
              >
                {mode === 'create' ? 'Create team' : 'Save changes'}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default ManageTeamModal;
